// src/pages/Login.jsx
// Now uses httpOnly cookies for authentication (XSS-safe)
// Uses AuthContext to manage authentication state
import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { login as apiLogin } from "../api";
import TwoFactorVerify from "../components/TwoFactorVerify";

export default function Login() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [logoError, setLogoError] = useState(false);

  // Check if session expired (redirected from 401)
  const searchParams = new URLSearchParams(location.search);
  const sessionExpired = searchParams.get('expired') === '1';

  // Account lockout state
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutMinutes, setLockoutMinutes] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);

  // 2FA state
  const [showMfa, setShowMfa] = useState(false);
  const [mfaTempToken, setMfaTempToken] = useState(null);
  const [mfaEmail, setMfaEmail] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setIsLocked(false);
    setAttemptsRemaining(null);

    try {
      const data = await apiLogin(email, pwd);

      // Check if MFA is required
      if (data.mfaRequired) {
        setMfaTempToken(data.tempToken);
        setMfaEmail(email);
        setShowMfa(true);
        return;
      }

      // Update auth context - the httpOnly cookie is already set by the backend
      authLogin(data.user);

      // Redirect based on role - agency roles go to agency dashboard
      const isAgencyRole = data.user?.role && ["agency", "agency_owner", "agency_admin"].includes(data.user.role);
      const defaultPath = isAgencyRole ? "/agency/dashboard" : "/dashboard";
      const from = location.state?.from?.pathname || defaultPath;
      nav(from, { replace: true });
    } catch (e) {
      // Handle account lockout
      if (e.locked) {
        setIsLocked(true);
        setLockoutMinutes(e.remainingMinutes || e.lockoutMinutes || 15);
        setErr(e.message);
        setPwd('');
        return;
      }

      // Show remaining attempts warning
      if (e.attemptsRemaining !== undefined && e.attemptsRemaining <= 2) {
        setAttemptsRemaining(e.attemptsRemaining);
      }

      setErr(typeof e?.message === "string" ? e.message : t('login') + " failed");
    }
  }

  // Handle successful MFA verification
  function handleMfaSuccess({ user }) {
    // Update auth context - the httpOnly cookie is already set by the backend
    authLogin(user);

    // Redirect based on role - agency roles go to agency dashboard
    const isAgencyRole = user?.role && ["agency", "agency_owner", "agency_admin"].includes(user.role);
    const defaultPath = isAgencyRole ? "/agency/dashboard" : "/dashboard";
    const from = location.state?.from?.pathname || defaultPath;
    nav(from, { replace: true });
  }

  // Handle MFA cancellation - go back to login
  function handleMfaCancel() {
    setShowMfa(false);
    setMfaTempToken(null);
    setMfaEmail("");
    setPwd(""); // Clear password for security
  }

  // Show 2FA verification screen if MFA is required
  if (showMfa) {
    return (
      <TwoFactorVerify
        tempToken={mfaTempToken}
        email={mfaEmail}
        onSuccess={handleMfaSuccess}
        onCancel={handleMfaCancel}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F2A43] via-[#0E3F73] to-[#0A69B8] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 overflow-hidden">
          {/* Top accent */}
          <div className="h-2 bg-amber-400" />

          {/* Header w/ logo */}
          <div className="px-8 pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-white shadow mx-auto -mt-10 ring-1 ring-black/5 flex items-center justify-center">
              {/* Uses your public/logo.png */}
              {!logoError ? (
                <img
                  src="/logo.png"
                  alt="Pro IRP"
                  className="w-12 h-12 object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <span className="text-xs font-semibold tracking-wider">PRO IRP</span>
              )}
            </div>

            <h1 className="mt-5 text-2xl font-bold text-slate-800">
              Pro <span className="text-amber-500">IRP</span>
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {t('nextGenInsurance')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="px-8 pt-6 pb-8">
            {/* Session Expired Notice */}
            {sessionExpired && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700 font-semibold mb-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Session Expired
                </div>
                <p className="text-amber-600 text-sm">
                  Your session has timed out for security. Please log in again.
                </p>
              </div>
            )}

            {/* Account Locked Warning */}
            {isLocked && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 font-semibold mb-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-6V9a4 4 0 10-8 0v4h12a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2h2" />
                  </svg>
                  Account Temporarily Locked
                </div>
                <p className="text-red-600 text-sm">
                  Too many failed login attempts. Please try again in {lockoutMinutes} minute{lockoutMinutes !== 1 ? 's' : ''}.
                </p>
              </div>
            )}

            {/* Attempts Remaining Warning */}
            {!isLocked && attemptsRemaining !== null && attemptsRemaining <= 2 && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-700 text-sm font-medium">
                  Warning: {attemptsRemaining} login attempt{attemptsRemaining !== 1 ? 's' : ''} remaining before account lockout.
                </p>
              </div>
            )}

            {/* Error Message */}
            {err && !isLocked ? (
              <div className="mb-3 text-red-600 text-sm">{err}</div>
            ) : null}

            <label htmlFor="email" className="block text-sm text-slate-700 mb-1">{t('emailLabel')}</label>
            <input
              id="email"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 mb-3"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <label htmlFor="password" className="block text-sm text-slate-700 mb-1">{t('passwordLabel')}</label>
            <input
              id="password"
              type="password"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 mb-5"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoComplete="current-password"
            />

            <button
              type="submit"
              className="w-full bg-slate-900 text-white rounded-lg py-2.5 font-medium hover:opacity-95 active:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLocked}
            >
              {isLocked ? `Locked (${lockoutMinutes}m)` : t('logIn')}
            </button>
          </form>

          {/* Footer links */}
          <div className="px-8 pb-6 text-center text-sm flex items-center justify-center gap-6">
            <Link to="/forgot" className="text-slate-500 hover:text-slate-700 underline">
              {t('forgotPassword')}
            </Link>
            <Link to="/signup" className="text-slate-500 hover:text-slate-700 underline">
              {t('signUp')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
